import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Observable, of, Subscription } from 'rxjs';
import { catchError, filter, mapTo, take, tap } from 'rxjs/operators';
import { IModalComponentRef, ModalOverlayService } from '@lib/modal';
import { extractQueryParam } from '@core/helpers';
import { HCConfigRules } from 'src/hc-config/hc-config';
import { IPtmSignerCertificateValidation } from '@project/view-models';
import { PtmSignerApiProviderService } from '@project/data-providers';
import { ModalPhysicianSignatureValidationComponent } from '../components/modal-physician-signature-validation/modal-physician-signature-validation.component';
import { IFeatureService } from './interfaces/feature-serivice.interface';
import { UserProfileDataService } from './user-profile-data.service';
import { HcService } from './hc-service';

const QR_CODE_RETURN_PARAM = 'code';

interface IValidationModalData {
  qrCodeUrl: string | null;
  doctorId: string | null;
  initialCode: string | null;
  missingCertificate: boolean;
  blockWorkspaceOnCancel: boolean;
  redirectsToLogin: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class PhysicianSignatureValidationFeatureService implements IFeatureService {
  private modalRef: IModalComponentRef<ModalPhysicianSignatureValidationComponent> | null = null;
  private modalCloseSubscription: Subscription | null = null;

  constructor(
    private modalOverlayService: ModalOverlayService,
    private ptmSignerApiProviderService: PtmSignerApiProviderService,
    private userProfileDataService: UserProfileDataService,
    private hcService: HcService,
    private router: Router,
  ) {}

  public initialise(): Observable<void> {
    if (!this.hcService.hasRule(HCConfigRules.PHYSICIAN_SIGNATURE_VALIDATION)) {
      if (!this.hcService.hasRule(HCConfigRules.PHYSICIAN_SIGNATURE_STATUS_WIDGET)) {
        return of(null);
      }

      return this.loadCertificateValidation().pipe(mapTo(null));
    }

    const returnedCode = this.getReturnedQrCode();
    if (returnedCode) {
      this.clearReturnedQrCode();
      this.openValidationModal({
        qrCodeUrl: null,
        doctorId: this.getDoctorId(),
        initialCode: returnedCode,
        missingCertificate: false,
        blockWorkspaceOnCancel: false,
        redirectsToLogin: false,
      });
      return of(null);
    }

    return this.loadCertificateValidation().pipe(
      tap((validation) => {
        if (validation && !this.isValidated(validation) && !this.blockMissingCertificate(validation, true)) {
          this.openValidationModal({
            qrCodeUrl: validation.qrCodeUrl,
            doctorId: this.getDoctorId(),
            initialCode: null,
            missingCertificate: false,
            blockWorkspaceOnCancel: true,
            redirectsToLogin: true,
          });
        }
      }),
      mapTo(null),
    );
  }

  public destroy(): Observable<void> {
    this.modalCloseSubscription?.unsubscribe();
    this.modalCloseSubscription = null;
    this.modalRef?.close();
    this.modalRef = null;
    return of(null);
  }

  public loadCertificateValidation(): Observable<IPtmSignerCertificateValidation | null> {
    const doctorId = this.getDoctorId();

    if (!doctorId) {
      return of(null);
    }

    return this.ptmSignerApiProviderService.validateDoctorCertificate(doctorId).pipe(catchError(() => of(null)));
  }

  public isValidated(validation: IPtmSignerCertificateValidation): boolean {
    return validation.cert && validation.token;
  }

  public startValidationFlow(): void {
    this.loadCertificateValidation()
      .pipe(take(1))
      .subscribe((validation) => {
        if (validation && !this.isValidated(validation) && !this.blockMissingCertificate(validation, false)) {
          this.openValidationModal({
            qrCodeUrl: validation.qrCodeUrl,
            doctorId: this.getDoctorId(),
            initialCode: null,
            missingCertificate: false,
            blockWorkspaceOnCancel: false,
            redirectsToLogin: false,
          });
        }
      });
  }

  private blockMissingCertificate(validation: IPtmSignerCertificateValidation, shouldBlockWorkspace: boolean): boolean {
    if (validation.cert) {
      return false;
    }

    this.openValidationModal({
      qrCodeUrl: null,
      doctorId: this.getDoctorId(),
      initialCode: null,
      missingCertificate: true,
      blockWorkspaceOnCancel: shouldBlockWorkspace,
      redirectsToLogin: shouldBlockWorkspace,
    });

    return true;
  }

  private openValidationModal(data: IValidationModalData): void {
    if (this.modalRef) {
      return;
    }

    this.modalRef = this.modalOverlayService.openOverlay<ModalPhysicianSignatureValidationComponent>(
      ModalPhysicianSignatureValidationComponent,
      data,
      { fullScreen: true, closeByBackdropClick: false },
    );

    this.modalCloseSubscription = this.modalRef.close$.pipe(take(1)).subscribe(() => {
      this.modalRef = null;
      this.modalCloseSubscription = null;
    });
  }

  private getDoctorId(): string | null {
    return this.userProfileDataService.profile?.id ?? null;
  }

  private getReturnedQrCode(): string | null {
    return extractQueryParam(window.location.href, QR_CODE_RETURN_PARAM);
  }

  /**
   * initialise() runs inside a route guard, while the router is still navigating: a URL changed
   * there is overwritten by the router at the end of the navigation, and the code would stay in
   * the URL (and be sent again on a reload). Remove it through the router once it has settled.
   */
  private clearReturnedQrCode(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        take(1),
      )
      .subscribe(() =>
        this.router.navigate([], {
          queryParams: { [QR_CODE_RETURN_PARAM]: null },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        }),
      );
  }
}
